/**
 * Leads from the public site: newsletter subscribers and free audits.
 *
 * Every lead row stores where the visitor came from (first and last marketing
 * touch from attribution.ts), so the admin can count leads per source and see
 * which of them later signed up and paid. GA4 gets one event per new lead:
 *   newsletter_signup  (form_location = which form)
 *   generate_lead      (lead_type = free_audit), with free_audit_start and
 *                      free_audit_completed around it for the funnel.
 */
import { supabase } from "@/integrations/supabase/client";
import { getAttribution, type Touch } from "@/lib/attribution";
import { pushEvent } from "@/lib/gtm";

type CompactTouch = { source: string; medium: string; campaign?: string; channel: string; landing_page: string; ts: string };

function compact(t: Touch | null): CompactTouch | null {
  if (!t) return null;
  return { source: t.source, medium: t.medium, campaign: t.campaign, channel: t.channel, landing_page: t.landing_page, ts: t.ts };
}

/** First and last touch, small enough to store on a lead row. */
export function leadAttribution(): { first: CompactTouch | null; last: CompactTouch | null } {
  const { first, last } = getAttribution();
  return { first: compact(first), last: compact(last) };
}

/** utm_* columns for lead tables: the last marketing touch (a direct visit never replaces it). */
export function leadUtm(): Record<string, string | null> {
  const { first, last } = getAttribution();
  const t = last ?? first;
  if (!t) return {};
  return {
    utm_source: t.source,
    utm_medium: t.medium,
    utm_campaign: t.campaign ?? null,
    utm_content: t.content ?? null,
    utm_term: t.term ?? null,
  };
}

/** "blog_article_my-post" → "blog_article": GA gets the form, not the page. */
export function formLocation(source: string): string {
  if (source.startsWith("blog_article")) return "blog_article";
  return source.replace(/[^a-z0-9_]/gi, "_").slice(0, 40) || "website";
}

type SubscribeInput = { email: string; source: string; firstName?: string; lastName?: string };
type SubscribeResult = { success?: boolean; already_subscribed?: boolean; message?: string; error?: string };

/**
 * The one way the site subscribes someone to the newsletter. Sends the
 * visitor's attribution with the request, and fires newsletter_signup only for
 * a real new subscriber: not for someone already on the list, and not for the
 * automatic opt-in at account signup (that person is counted as sign_up).
 */
export async function subscribeNewsletter({ email, source, firstName, lastName }: SubscribeInput) {
  const res = await supabase.functions.invoke<SubscribeResult>("newsletter-subscribe", {
    body: { email, firstName, lastName, source, attribution: leadAttribution() },
  });
  if (!res.error && res.data?.success && !res.data.already_subscribed && source !== "signup") {
    pushEvent("newsletter_signup", { form_location: formLocation(source) });
  }
  return res;
}
